/*
 * assetLogos.js — resolve REAL token & chain logos from public CDNs.
 *
 * Resolution order at every call site: DB logoUrl → CDN(s) → colored glyph.
 *
 * Two things used to break this:
 *
 *  1. Separators. resolveChainKey() stripped the word "testnet" but not the
 *     separator around it, so `bsc-testnet` became `bsc-` and matched nothing.
 *     Since slugs (`bsc-testnet`, `sepolia-ethereum`, `polygon-pos`,
 *     `arbitrum-one`, `ethereum-mainnet`) are what most call sites pass, those
 *     chains had NO CDN fallback at all — one dead DB url and the logo was gone.
 *
 *  2. Placeholder DB urls. Several networks store
 *     `chainlist.org/unknown-logo.png`, and the *scan.io asset paths 403 on
 *     hotlink. A url that resolves to a generic "unknown" image is worse than no
 *     url, because it satisfies <img> and the real CDN is never tried. Those are
 *     now treated as absent (see isUsableLogoUrl).
 */

// Token icon sets. cryptocurrency-icons is cleaner; coincap covers more
// symbols (SHIB, CAKE, POL, CRO...). Both are tried before the glyph.
const CRYPTO_ICONS = (sym) =>
  `https://cdn.jsdelivr.net/npm/cryptocurrency-icons@0.18.1/svg/color/${sym}.svg`;

const COINCAP_ICONS = (sym) => `https://assets.coincap.io/assets/icons/${sym}@2x.png`;

const LLAMA_CHAIN = (slug) => `https://icons.llamao.fi/icons/chains/rsz_${slug}.jpg`;

// Urls that "load" but carry no information, or that reject hotlinking. Treated
// as if the field were empty so the CDN fallback gets its turn.
const PLACEHOLDER_LOGO_PATTERNS = [
  /unknown-logo/i,
  /empty-token/i,
  /\bplaceholder\b/i,
  /\bdefault-(token|chain|logo)\b/i,
  // block explorer asset paths that 403 for off-site requests
  /^https?:\/\/testnet\.bscscan\.com\//i,
  /^https?:\/\/logo\.svgcdn\.com\//i,
];

/** False for empty, non-http, or known-useless logo urls. */
export const isUsableLogoUrl = (url) => {
  const s = String(url ?? '').trim();
  if (!s) return false;
  if (!/^https?:\/\//i.test(s) && !s.startsWith('/')) return false;
  return !PLACEHOLDER_LOGO_PATTERNS.some((re) => re.test(s));
};

// Token symbol → the symbol the icon sets file it under.
const TOKEN_ALIASES = {
  POL: 'matic',
  WMATIC: 'matic',
  WETH: 'eth',
  WBNB: 'bnb',
  WBTC: 'btc',
  WBETH: 'eth',
  'USDT.E': 'usdt',
  'USDC.E': 'usdc',
  'DAI.E': 'dai',
  'WETH.E': 'eth',
};

// Brand colors for the fallback glyph.
export const TOKEN_COLORS = {
  USDC: '#2775ca', USDT: '#26a17b', BTC: '#f7931a', WBTC: '#f7931a',
  ETH: '#627eea', WETH: '#627eea', WBETH: '#627eea', SOL: '#9945ff',
  DAI: '#f5ac37', BUSD: '#f0b90b', TUSD: '#2b2e7f', FDUSD: '#c8a951',
  MATIC: '#8247e5', POL: '#8247e5', BNB: '#f0b90b', TRX: '#eb0029',
  AVAX: '#e84142', ARB: '#28a0f0', OP: '#ff0420', CHZ: '#cd0124',
  CAKE: '#d1884f', LINK: '#2a5ada', UNI: '#ff007a', SHIB: '#f00500',
  DOGE: '#c2a633', XRP: '#23292f', ADA: '#0033ad', DOT: '#e6007a',
  LTC: '#345d9d', NEAR: '#00c08b', ATOM: '#2e3148', XLM: '#08b5e5',
  AAVE: '#b6509e', CRV: '#40649f', SNX: '#00d1ff', MANA: '#ff2d55',
  SAND: '#00adef', CRO: '#002d74', LEO: '#f0b90b', TON: '#0098ea',
  THETA: '#2ab8e6', QNT: '#1a1a1a', LDO: '#00a3ff', LPT: '#00eb88',
  FLOW: '#00ef8b', WOO: '#3d3ee6', APE: '#0344dc', '1INCH': '#1a2c50',
};

export const tokenColor = (symbol) =>
  TOKEN_COLORS[String(symbol ?? '').trim().toUpperCase()] || '#6b7280';

export const tokenInitials = (symbol) => {
  const s = String(symbol ?? '').trim().toUpperCase();
  if (!s) return '?';
  return s.slice(0, s.length <= 4 ? 1 : 2);
};

/** Every CDN candidate for a token symbol, best first. */
export const tokenLogoUrls = (symbol) => {
  const raw = String(symbol ?? '').trim().toUpperCase();
  if (!raw) return [];
  const sym = (TOKEN_ALIASES[raw] || raw).toLowerCase();
  const urls = [CRYPTO_ICONS(sym), COINCAP_ICONS(sym)];
  // If the symbol was aliased, also try the original spelling.
  const original = raw.toLowerCase();
  if (original !== sym) urls.push(CRYPTO_ICONS(original), COINCAP_ICONS(original));
  return urls;
};

/** Primary CDN candidate. Kept for existing callers. */
export const tokenLogoUrl = (symbol) => tokenLogoUrls(symbol)[0] || null;

// Canonical chain key → display label + DefiLlama slug + native asset symbol.
// `native` gives every chain a logo from the token icon set, which is the only
// CORS-open source we have — needed wherever the bytes must be read rather than
// just displayed (canvas, PDF export). L2s show ETH, which is what they settle in.
const CHAINS = {
  ethereum: { label: 'Ethereum', llama: 'ethereum', native: 'ETH' },
  bsc: { label: 'BNB Chain', llama: 'binance', native: 'BNB' },
  polygon: { label: 'Polygon', llama: 'polygon', native: 'POL' },
  arbitrum: { label: 'Arbitrum', llama: 'arbitrum', native: 'ETH' },
  optimism: { label: 'Optimism', llama: 'optimism', native: 'ETH' },
  base: { label: 'Base', llama: 'base', native: 'ETH' },
  avalanche: { label: 'Avalanche', llama: 'avalanche', native: 'AVAX' },
  tron: { label: 'Tron', llama: 'tron', native: 'TRX' },
  solana: { label: 'Solana', llama: 'solana', native: 'SOL' },
  bitcoin: { label: 'Bitcoin', llama: 'bitcoin', native: 'BTC' },
};

// Everything is matched after normalizeKey(), so keys here use single spaces.
const CHAIN_ALIASES = {
  binance: 'bsc', bnb: 'bsc', 'bnb chain': 'bsc', 'bnb smart chain': 'bsc',
  'binance smart chain': 'bsc', bep20: 'bsc', bep2: 'bsc', 'bsc mainnet': 'bsc',
  eth: 'ethereum', erc20: 'ethereum', 'eth mainnet': 'ethereum',
  matic: 'polygon', pol: 'polygon', 'polygon pos': 'polygon',
  'polygon mainnet': 'polygon',
  arb: 'arbitrum', 'arbitrum one': 'arbitrum', 'arbitrum nova': 'arbitrum',
  op: 'optimism', 'op mainnet': 'optimism',
  btc: 'bitcoin', trx: 'tron', trc20: 'tron', sol: 'solana', avax: 'avalanche',
  'avalanche c chain': 'avalanche', 'avalanche cchain': 'avalanche',
};

// Both mainnet and testnet chain ids, so a numeric chainId always resolves.
const CHAIN_BY_ID = {
  1: 'ethereum', 5: 'ethereum', 11155111: 'ethereum', 17000: 'ethereum',
  56: 'bsc', 97: 'bsc',
  137: 'polygon', 80001: 'polygon', 80002: 'polygon',
  42161: 'arbitrum', 421614: 'arbitrum',
  10: 'optimism', 11155420: 'optimism',
  8453: 'base', 84532: 'base',
  43114: 'avalanche', 43113: 'avalanche',
  728126428: 'tron', 2494104990: 'tron',
};

// Words that only say WHICH deployment of a chain this is. A testnet shares its
// parent chain's icon, which is what the product wants: BSC-Testnet shows the
// BNB Chain icon, Sepolia shows the Ethereum icon.
const DEPLOYMENT_WORDS = /\b(testnet|mainnet|sepolia|goerli|holesky|amoy|mumbai|fuji|devnet|rinkeby|ropsten|kovan|shasta|nile|test|net)\b/g;

// Lowercase, turn -/_/. into spaces, collapse runs. Without this, stripping
// "testnet" out of "bsc-testnet" left the dangling "bsc-".
const normalizeKey = (input) =>
  String(input ?? '')
    .trim()
    .toLowerCase()
    .replace(/[-_./]+/g, ' ')
    .replace(/\s*\(.*?\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const lookup = (key) => {
  if (!key) return null;
  if (CHAINS[key]) return key;
  if (CHAIN_ALIASES[key]) return CHAIN_ALIASES[key];
  if (/^\d+$/.test(key) && CHAIN_BY_ID[Number(key)]) return CHAIN_BY_ID[Number(key)];
  return null;
};

export const resolveChainKey = (input) => {
  if (input == null) return null;
  if (typeof input === 'number') return CHAIN_BY_ID[input] || null;

  const base = normalizeKey(input);
  if (!base) return null;

  // 1. as given
  const direct = lookup(base);
  if (direct) return direct;

  // 2. with deployment words removed ("bsc testnet" -> "bsc",
  //    "sepolia ethereum" -> "ethereum", "ethereum mainnet" -> "ethereum")
  const stripped = base.replace(DEPLOYMENT_WORDS, ' ').replace(/\s+/g, ' ').trim();
  const viaStripped = lookup(stripped);
  if (viaStripped) return viaStripped;

  // 3. any single remaining word ("polygon pos" -> "polygon",
  //    "arbitrum one" -> "arbitrum", "op stack chain" -> "op")
  for (const word of stripped.split(' ')) {
    const viaWord = lookup(word);
    if (viaWord) return viaWord;
  }

  return null;
};

export const chainMeta = (input) => {
  const key = resolveChainKey(input);
  return key ? CHAINS[key] : null;
};

export const chainLogoUrl = (input) => {
  const m = chainMeta(input);
  return m ? LLAMA_CHAIN(m.llama) : null;
};

/**
 * Every CDN candidate for a chain logo, best first.
 *
 * The DefiLlama icon comes first because it is the chain's actual brand mark.
 * But on its own it is a single point of failure, and unevenly so: most of our
 * networks store a working logo_url and never reach this list, while
 * bsc-testnet (hotlink-blocked bscscan url), sepolia-ethereum and
 * ethereum-mainnet (both chainlist.org/unknown-logo.png) have their stored url
 * rejected by isUsableLogoUrl and so have DefiLlama as their ONLY source. One
 * blocked domain -- an extension, a DNS filter, a CDN blip -- and precisely
 * those three render a letter glyph while every other network looks fine.
 *
 * The chain's native asset closes that gap: it resolves through the same icon
 * CDNs the token logos already use, so it can only fail if token logos are
 * failing too. L2s show ETH, which is what they settle in. This is the same
 * substitution invoicePdf.js already makes, there because DefiLlama sends no
 * CORS header and its bytes cannot be read into a canvas.
 */
export const chainLogoUrls = (input) => {
  const m = chainMeta(input);
  if (!m) return [];
  return [LLAMA_CHAIN(m.llama), ...(m.native ? tokenLogoUrls(m.native) : [])];
};

/** The chain's native asset symbol (BSC -> BNB, Sepolia -> ETH), or null. */
export const chainNativeSymbol = (input) => {
  const m = chainMeta(input);
  return m?.native || null;
};

export const chainDisplayLabel = (input) => {
  const m = chainMeta(input);
  if (m) return m.label;
  const s = String(input ?? '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};
